import React, { useState } from 'react'

import styled from 'styled-components'
import Joi from 'joi'

import CharacterReferenceListItem from './CharacterReferenceListItem'
import CharacterReferenceForm from './CharacterReferenceForm'
import EmptyWrapper from '../../../../../../components/EmptyWrapper'

const CharacterReference = ({
  onChange,
  characterReference,
  setCharacterReference,
  characterReferences,
  setCharacterReferences,
  fieldErrors,
  setFieldErrors,
}) => {
  const [showForm, setShowForm] = useState(false)
  const [isEdit, setIsEdit] = useState(false)

  const defaultFields = () =>
    setCharacterReference({
      id: Date.now(),
      name: '',
      email: '',
      phone: '',
    })

  const validateData = (payload) => {
    const educationalBackgroundSchema = Joi.object({
      id: Joi.number().label('Id').required(),
      name: Joi.string().label('Name').required(),
      email: Joi.string()
        .label('Email Address')
        .email({ tlds: { allow: false } })
        .required()
        .messages({
          'string.base': '"Email Address" must be a text value',
          'string.email': '"Email Address" must be a valid email address',
          'any.required': '"Email Address" is required',
        }),
      phone: Joi.string().label('Contact Number').required(),
    })

    const result = educationalBackgroundSchema.validate(payload, {
      abortEarly: false,
    })

    if (result.error) {
      const { error } = result
      const fieldNames = Object.keys(payload)
      const updatedErrors = fieldNames.reduce((acc, fieldName) => {
        acc[fieldName] =
          error?.details
            .find((detail) => detail.context.key === fieldName)
            ?.message.replace(/"/g, '') || ''
        return acc
      }, {})

      setFieldErrors((prev) => ({
        ...prev,
        characterReference: { ...updatedErrors },
      }))
      return false
    } else {
      setFieldErrors((prev) => ({
        ...prev,
        characterReference: {},
      }))
      return true
    }
  }

  const handleEdit = (data) => {
    setShowForm(true)
    setCharacterReference(data)
    setIsEdit(true)
  }

  const handleDelete = (id) => {
    setShowForm(false)
    const updatedBackgrounds = characterReferences.filter((e) => e.id !== id)
    setCharacterReferences(updatedBackgrounds)
    setIsEdit(false)
  }

  const handleCloseForm = () => {
    setShowForm(false)
    setIsEdit(false)
  }

  const handleAddCharacterReference = () => {
    if (!showForm) {
      setShowForm(true)
      defaultFields()
    } else {
      if (!validateData(characterReference)) return

      if (isEdit) {
        const selected = characterReferences.filter(
          (e) => e.id === characterReference?.id
        )[0]

        const updated = {
          ...characterReference,
          id: selected.id,
        }

        setCharacterReferences(
          characterReferences.map((e) => (e.id === updated.id ? updated : e))
        )
        setIsEdit(false)
      } else {
        setCharacterReferences([
          ...characterReferences,
          {
            id: Date.now(),
            ...characterReference,
          },
        ])
      }

      defaultFields()

      setShowForm(false)
    }
  }

  const handleChange = (name, value) => {
    onChange({ [name]: value })
  }

  return (
    <>
      <CharacterReferenceListItemContainer>
        {characterReferences?.length > 0 ? (
          characterReferences?.map((data, index) => (
            <CharacterReferenceListItem
              key={index}
              id={data.id}
              name={data.name}
              email={data.email}
              phone={data.phone}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          ))
        ) : (
          <EmptyWrapper message="No character references." />
        )}
      </CharacterReferenceListItemContainer>
      <CharacterReferenceForm
        characterReference={characterReference}
        onChange={handleChange}
        showForm={showForm}
        onAddCharacterReference={handleAddCharacterReference}
        onClose={handleCloseForm}
        isEdit={isEdit}
        fieldErrors={fieldErrors}
      />
    </>
  )
}

const CharacterReferenceListItemContainer = styled.div`
  max-height: 400px;
  overflow: auto;
  margin-bottom: 1rem;
`

export default CharacterReference
